import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { type VariantProps, cva } from "class-variance-authority";
import { AnimatePresence, m } from "framer-motion";
import { ChevronDown } from "lucide-react";
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  createContext,
  forwardRef,
  useContext,
  useState,
} from "react";
import { cn } from "../cn";
import { springSoft, useMotionTransition } from "../motion";
import { useSheetPortalContainer } from "./Sheet";

/**
 * Accessible dropdown menu (Radix DropdownMenu) with framer enter/exit, built
 * like {@link Dialog}: Radix owns the behaviour (roving focus, typeahead,
 * escape and outside-click dismissal, `role="menu"`/`role="menuitem"`, returning
 * focus to the trigger), framer adds the motion through `forceMount` +
 * `AnimatePresence`.
 *
 * Composed rather than prop-driven (`DropdownMenu` + `DropdownMenuTrigger` +
 * `DropdownMenuContent` + `DropdownMenuItem`), matching `Card` and `Table`, so a
 * caller renders whatever its menu needs instead of feeding an items array
 * through an ever-growing prop.
 *
 * The content is portalled, so it is never clipped by an ancestor's
 * `overflow-hidden` and needs no z-index juggling at the call site.
 */

/**
 * Whether the menu is open, shared with {@link DropdownMenuContent}.
 *
 * `AnimatePresence` can only animate an exit if it sees the content unmount, and
 * Radix's `forceMount` deliberately keeps it mounted. So the open state has to
 * be readable one level down, and this context is how it gets there for the
 * uncontrolled case too.
 */
const DropdownMenuOpenContext = createContext(false);

export interface DropdownMenuProps
  extends ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root> {}

export function DropdownMenu({
  open,
  defaultOpen,
  onOpenChange,
  children,
  // Non-modal by default: a modal Radix menu puts `pointer-events: none` on
  // `<body>` and restores it on unmount. Because the content is `forceMount`ed
  // for its exit animation, that unmount lands *after* a dialog opened from a
  // menu item has taken over the same body style, so closing the dialog
  // restores the menu's `none` and the whole page stops accepting clicks.
  modal = false,
  ...props
}: DropdownMenuProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(
    defaultOpen ?? false,
  );
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : uncontrolledOpen;

  return (
    <DropdownMenuPrimitive.Root
      open={isOpen}
      modal={modal}
      onOpenChange={(next) => {
        if (!isControlled) {
          setUncontrolledOpen(next);
        }
        onOpenChange?.(next);
      }}
      {...props}
    >
      <DropdownMenuOpenContext.Provider value={isOpen}>
        {children}
      </DropdownMenuOpenContext.Provider>
    </DropdownMenuPrimitive.Root>
  );
}

export interface DropdownMenuTriggerProps
  extends ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> {}

/**
 * The control that opens the menu. Defaults to `asChild`, because the trigger is
 * almost always an existing `Button` or icon button that should keep its own
 * chrome and accessible name.
 *
 * Muted until hovered, which is the treatment every trigger in the app was
 * repeating by hand. Pass a text colour through `className` to override it.
 */
export const DropdownMenuTrigger = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  DropdownMenuTriggerProps
>(({ asChild = true, className, ...props }, ref) => (
  <DropdownMenuPrimitive.Trigger
    ref={ref}
    asChild={asChild}
    className={cn(
      "text-muted-foreground transition-colors hover:text-foreground",
      className,
    )}
    {...props}
  />
));
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

/**
 * Chevron for a labelled {@link DropdownMenuTrigger}, rotating with the menu's
 * own open state so a caller never has to mirror that state itself. Colour is
 * inherited from the trigger.
 */
export function DropdownMenuChevron({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof ChevronDown>) {
  const open = useContext(DropdownMenuOpenContext);

  return (
    <ChevronDown
      aria-hidden="true"
      className={cn(
        "size-4 transition-transform",
        open && "rotate-180",
        className,
      )}
      {...props}
    />
  );
}

export interface DropdownMenuContentProps
  extends ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> {}

export const DropdownMenuContent = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(({ className, align = "end", sideOffset = 6, children, ...props }, ref) => {
  const open = useContext(DropdownMenuOpenContext);
  const transition = useMotionTransition(springSoft);
  // When rendered inside a Sheet, portal into its panel rather than `body`. The
  // Sheet is a modal Radix Dialog: while open it sets `pointer-events: none` on
  // `<body>` and traps focus in its panel, so a body-portalled menu can take
  // neither focus nor clicks and appears not to open at all. Same reason the
  // Combobox, MultiSelect and DatePicker popovers portal here.
  const sheetContainer = useSheetPortalContainer();

  return (
    <AnimatePresence>
      {open && (
        <DropdownMenuPrimitive.Portal
          forceMount
          container={sheetContainer ?? undefined}
        >
          <DropdownMenuPrimitive.Content
            ref={ref}
            asChild
            forceMount
            align={align}
            sideOffset={sideOffset}
            {...props}
          >
            {/*
              `initial` stays a real hidden state even under reduced motion
              (unlike an entrance in normal flow, which can skip straight to its
              target). The content is portalled and positioned by Radix's Popper
              only after it mounts, so painting it opaque on the first frame
              would flash it at the top-left before it snaps into place. Starting
              from `opacity: 0` hides that frame; the reduced-motion transition
              from `useMotionTransition` is instant, so it still appears without
              animating.
            */}
            <m.div
              initial={{ opacity: 0, scale: 0.96, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: -2 }}
              transition={transition}
              className={cn(
                "z-50 min-w-52 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-lg focus:outline-none",
                className,
              )}
            >
              {children}
            </m.div>
          </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
      )}
    </AnimatePresence>
  );
});
DropdownMenuContent.displayName = "DropdownMenuContent";

/**
 * Item metrics and highlight treatment are deliberately identical to the
 * options in {@link Combobox}: both are rows in a popover list, so a menu item
 * and an option must not read as two different components. Radix flips
 * `data-[highlighted]` on both pointer hover and keyboard navigation, which is
 * the direct equivalent of the combobox's `activeIndex`.
 */
const dropdownMenuItemVariants = cva(
  "flex w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      tone: {
        default:
          "text-foreground data-[highlighted]:bg-primary-soft data-[highlighted]:text-primary",
        destructive:
          "text-destructive data-[highlighted]:bg-destructive/10 data-[highlighted]:text-destructive",
      },
    },
    defaultVariants: { tone: "default" },
  },
);

export interface DropdownMenuItemProps
  extends ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>,
    VariantProps<typeof dropdownMenuItemVariants> {}

export const DropdownMenuItem = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>(({ className, tone, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(dropdownMenuItemVariants({ tone }), className)}
    {...props}
  />
));
DropdownMenuItem.displayName = "DropdownMenuItem";

export { dropdownMenuItemVariants };
